import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { GoSidebarCollapse, GoSidebarExpand } from 'react-icons/go';
import { PiCaretRightBold } from 'react-icons/pi';
import styled from 'styled-components';
import askVantageLogoLight from '../../../../images/ask_vantage_logo.svg';
import askVantageLogoDark from '../../../../images/ask_vantage_logo_dark.svg';
import { useAskVantage } from '../../context/AskVantageProvider';
import { useSpeechToText } from '../../hooks/useSpeechToText';
import { useTheme } from '../../theme';
import Tooltip from '../Tooltip';
import UsageReportPopover from '../UsageReportPopover';
import VoiceInputButton from '../VoiceInputButton';

interface MessageBoxProps {
  isSticky?: boolean;
  placeholder?: string;
  className?: string;
}

const MessageBox: React.FC<MessageBoxProps> = ({
  isSticky = false,
  placeholder = "How's my sales going this year compared from last year?",
  className = ''
}) => {
  const {
    send,
    isQuotaExceeded,
    error,
    clearError,
    sidebarOpen,
    toggleSidebar,
    createConversation,
    thinkingState,
    registerFillMessageBox
  } = useAskVantage();
  const theme = useTheme();

  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Speech-to-text functionality
  const {
    transcript,
    isListening,
    isSupported: speechSupported,
    error: speechError,
    currentLanguage,
    startListening,
    stopListening,
    resetTranscript,
    setLanguage
  } = useSpeechToText({
    continuous: false,
    interimResults: true
  });

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 160) + 'px';
    }
  }, [input]);

  // Focus handler for Cmd/Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        textareaRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle speech-to-text transcript updates
  useEffect(() => {
    if (transcript) {
      setInput(transcript);
    }
  }, [transcript]);

  // Register fillMessageBox function with context
  useEffect(() => {
    const handleFillMessageBox = (text: string) => {
      setInput(text);
      textareaRef.current?.focus();
    };

    registerFillMessageBox(handleFillMessageBox);

    return () => {
      registerFillMessageBox(() => {});
    };
  }, [registerFillMessageBox]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim()) {
      try {
        await send(input.trim());
        setInput('');
        resetTranscript();
        if (error) clearError();
      } catch (err) {
        console.error('Failed to send message:', err);
      }
    }
  };

  const handleMicrophoneClick = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const sidebarTooltipContent = useMemo(
    () => (sidebarOpen ? 'Close sidebar' : 'Open sidebar'),
    [sidebarOpen]
  );

  const askVantageLogo = useMemo(() => {
    return theme.mode === 'dark' ? askVantageLogoDark : askVantageLogoLight;
  }, [theme.mode]);

  return (
    <Wrapper className={className} isSticky={isSticky} $theme={theme}>
      {/* Fade gradient overlay when sticky */}
      {isSticky && <FadeOverlay $theme={theme} />}

      {(error || speechError) && (
        <ErrorBanner $theme={theme}>
          <ErrorText>{error || speechError}</ErrorText>
          <ErrorCloseButton
            onClick={() => {
              clearError();
              resetTranscript();
            }}
            $theme={theme}
          >
            ×
          </ErrorCloseButton>
        </ErrorBanner>
      )}

      <Form onSubmit={handleSubmit} role="form" isSticky={isSticky}>
        <UsageReportPopoverWrapper>
          <UsageReportPopover />
        </UsageReportPopoverWrapper>
        <InputContainer $theme={theme} disabled={isQuotaExceeded} isSticky={isSticky}>
          {/* First Row: Text Input */}
          <FirstRow>
            <TextareaContainer>
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  isQuotaExceeded
                    ? 'You have exhausted tokens. Please purchase more tokens to continue.'
                    : isListening
                      ? 'I’m listening. What would you like to ask?'
                      : placeholder
                }
                disabled={isQuotaExceeded || Boolean(thinkingState)}
                $theme={theme}
              />
            </TextareaContainer>
          </FirstRow>

          {/* Second Row: Logo + Action Buttons */}
          <SecondRow>
            {/* Left utility icons */}
            <UtilityIcons>
              <Tooltip content={sidebarTooltipContent} position="bottom" delay={1000}>
                <IconButton
                  type="button"
                  onClick={toggleSidebar}
                  aria-label={sidebarTooltipContent}
                  $theme={theme}
                >
                  {sidebarOpen ? <GoSidebarExpand size={20} /> : <GoSidebarCollapse size={20} />}
                </IconButton>
              </Tooltip>
              <LogoContainer>
                <Logo src={askVantageLogo} alt="Ask Vantage" />
              </LogoContainer>
            </UtilityIcons>

            {/* Right side: Create new conversation + Voice input + Send */}
            <RightActions>
              <Tooltip content="New conversation" position="bottom">
                <IconButton
                  type="button"
                  onClick={() => {
                    createConversation();
                    setInput('');
                    textareaRef.current?.focus();
                  }}
                  aria-label={'Create new conversation'}
                  $theme={theme}
                >
                  <FiPlus size={20} />
                </IconButton>
              </Tooltip>
              <VoiceInputButton
                currentLanguage={currentLanguage}
                onLanguageChange={setLanguage}
                isListening={isListening}
                onMicrophoneClick={handleMicrophoneClick}
                theme={theme}
                disabled={isQuotaExceeded || Boolean(thinkingState)}
                isSupported={speechSupported}
              />
              <SendButton
                type="submit"
                disabled={!input.trim() || isQuotaExceeded}
                $theme={theme}
                aria-label="Send message"
              >
                {thinkingState ? (
                  <Spinner $theme={theme} />
                ) : (
                  <PiCaretRightBold size={26} color="white" />
                )}
              </SendButton>
            </RightActions>
          </SecondRow>
        </InputContainer>
      </Form>

      {isSticky && (
        <DisclaimerContainer $theme={theme}>
          <DisclaimerText $theme={theme}>
            Ask Vantage can make mistakes. Always double-check key information.
          </DisclaimerText>
        </DisclaimerContainer>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div<{
  isSticky: boolean;
  $theme: any;
}>`
  ${(props) =>
    props.isSticky
      ? `
    position: sticky;
    bottom: -10px;
    z-index: 10;
    padding: 0px;
    padding-bottom: 10px;
    background-color: ${props.$theme.colors.mainBg};
    
    @media (max-width: 768px) {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 1001;
      padding: 16px;
      padding-bottom: max(16px, env(safe-area-inset-bottom));
      background-color: ${props.$theme.colors.mainBg};
      border-top: 1px solid ${props.$theme.colors.messageBoxBorder};
    }
  `
      : `
    max-width: 820px;
    margin: 0 auto;
    padding: 0 ${props.$theme.tokens.spaceLg};
  `}
`;

const ErrorBanner = styled.div<{ $theme: any }>`
  background-color: #ffebee;
  border: 1px solid ${(props) => props.$theme.colors.messageBoxBorder};
  border-radius: ${(props) => props.$theme.tokens.radiusLg};
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  animation: slideDown 0.3s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ErrorText = styled.span`
  color: #d32f2f;
  font-size: 0.9rem;
`;

const ErrorCloseButton = styled.button<{ $theme: any }>`
  background: none;
  border: none;
  color: #d32f2f;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  padding: 4px 8px;
  border-radius: 50%;

  &:hover {
    background-color: rgba(211, 47, 47, 0.1);
  }
`;

const Form = styled.form<{ isSticky: boolean }>`
  ${(props) =>
    props.isSticky
      ? `
    position: relative;
    bottom: -20px;`
      : ``}
  width: 100%;
  position: relative;
`;

const UsageReportPopoverWrapper = styled.div`
  position: relative;
  top: 25px;
  right: -20px;
  z-index: 10;
  max-width: 820px;
  width: 100%;
  margin: 0 auto;
  pointer-events: none;
  display: flex;
  justify-content: flex-end;
  padding-right: 8px;

  > * {
    pointer-events: auto;
  }

  @media (max-width: 768px) {
    padding-right: 0;
    top: 20px;
    right: -8px;
  }
`;

const InputContainer = styled.div<{
  $theme: any;
  disabled: boolean;
  isSticky: boolean;
}>`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.$theme.colors.messageBoxBg};
  border: 1px solid ${(props) => props.$theme.colors.messageBoxBorder};
  border-radius: 25px;
  padding: 16px;
  gap: 12px;
  transition: all ${(props) => props.$theme.tokens.transitionFast};
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};
  min-height: 80px;
  max-width: 820px;
  margin: 0 auto;
  box-shadow: ${(props) => (props.isSticky ? '0px 15px 20px 0px #0000001A' : 'none')};

  &:focus-within {
    border-color: ${(props) => props.$theme.tokens.primary};
    box-shadow: 0 0 0 3px ${(props) => props.$theme.tokens.primary}20;
  }

  @media (max-width: 768px) {
    ${(props) =>
      props.isSticky
        ? `
      margin: 0;
      border-radius: 20px;
      gap: 10px;
      padding: 12px;
      min-height: 72px;
      width: 100%;
      box-shadow: none;
    `
        : `
      margin: 0 ${props.$theme.tokens.spaceLg};
      gap: 10px;
      padding: 12px;
      min-height: 72px;
    `}
  }
`;

const FirstRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const SecondRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UtilityIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconButton = styled.button<{
  $theme: any;
  isListening?: boolean;
}>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  font-size: 16px;
  transition: all 200ms ease;
  color: ${(props) => (props.isListening ? '#dc3545' : props.$theme.colors.mainText)};

  &:hover {
    background-color: ${(props) => props.$theme.colors.messageBoxBorder};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${(props) =>
    props.isListening &&
    `
    animation: pulse 1.5s infinite;
    
    @keyframes pulse {
      0% {
        transform: scale(1);
        opacity: 1;
      }
      50% {
        transform: scale(1.1);
        opacity: 0.7;
      }
      100% {
        transform: scale(1);
        opacity: 1;
      }
    }
  `}
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const Logo = styled.img`
  height: 24px;
  width: auto;
`;

const TextareaContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  min-height: 40px;
`;

const Textarea = styled.textarea<{ $theme: any }>`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: ${(props) => props.$theme.colors.mainText};
  font-size: 16px;
  font-family: inherit;
  resize: none;
  min-height: 24px;
  max-height: 160px;
  line-height: 1.5;

  &::placeholder {
    color: ${(props) => props.$theme.colors.messageBoxPlaceholder};
  }

  &:disabled {
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    font-size: 14px;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    line-height: 1.4;
  }

  @media (max-width: 375px) {
    font-size: 12px;
  }
`;

const SendButton = styled.button<{ $theme: any }>`
  background: #262626;
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${(props) => props.$theme.tokens.transitionFast};
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: #262626;
    transform: scale(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

const Spinner = styled.div<{ $theme: any }>`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-left-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const DisclaimerContainer = styled.div<{ $theme: any }>`
  position: relative;
  bottom: -20px;
  text-align: center;
  padding: 10px 0;
  max-width: 820px;
  margin: 0 auto;
`;

const DisclaimerText = styled.p<{ $theme: any }>`
  color: ${(props) => props.$theme.colors.mainText};
  opacity: 0.6;
  font-size: 12px !important;
  margin: 0;
  line-height: 1.4;

  @media (max-width: 480px) {
    font-size: 11px !important;
  }

  @media (max-width: 375px) {
    font-size: 10px !important;
  }
`;

const FadeOverlay = styled.div<{ $theme: any }>`
  position: absolute;
  top: -80px; /* Position above the MessageBox */
  left: 0;
  right: 0;
  height: 80px; /* Height of the fade effect */
  background: linear-gradient(
    to bottom,
    transparent 0%,
    ${(props) => props.$theme.colors.mainBg}40 20%,
    ${(props) => props.$theme.colors.mainBg}80 70%,
    ${(props) => props.$theme.colors.mainBg} 100%
  );
  pointer-events: none; /* Allow clicks to pass through */
  z-index: 1; /* Above background but below content */

  @media (max-width: 768px) {
    height: 60px;
    top: -60px;
  }
`;

export default MessageBox;
